import { useMutation } from '@apollo/client';
import {
  DirectionalHint,
  FontIcon,
  IconButton,
  Link,
  Stack,
  Text,
  Theme,
  TooltipHost,
  useTheme,
} from '@fluentui/react';
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
  ResponderProvided,
} from '@hello-pangea/dnd';
import { showCustomToast } from 'common/components/CustomToast/utils';
import { StatusIcon } from 'common/components/StatusIcon';
import { useCommonStyles } from 'common/styles';
import React, { useEffect, useState } from 'react';
import {
  ApprovalFilter,
  ApprovalsOrderBy,
  Exact,
  InputMaybe,
  ResequenceTypeInput,
  Scalars,
} from 'common/types/globalTypes';
import { ColorHighLights } from '../ApprovalFlowDesigner/Body/GeneralInfo/BasicForm/constants';
import {
  ApprovalsInfo,
  ApprovalsInfo_approvals_nodes,
} from '../__generated__/ApprovalsInfo';
import APPROVAL_RESEQUENCING from './ApprovalResequencing.graphql';
import {
  ApprovalResequencing,
  ApprovalResequencingVariables,
} from './__generated__/ApprovalResequencing';
import { useStyles } from './index.styles';

interface ReorderHierarchyProps {
  approvalsInfo: ApprovalsInfo | undefined;
  loading: boolean;
  onRowOpen: (id: string) => void;
  refetch: (
    variables?:
      | Partial<
          Exact<{
            first?: InputMaybe<Scalars['Int']['input']>;
            after?: InputMaybe<Scalars['Cursor']['input']>;
            orderBy?: InputMaybe<Array<ApprovalsOrderBy> | ApprovalsOrderBy>;
            filter?: InputMaybe<ApprovalFilter>;
          }>
        >
      | undefined
  ) => Promise<any>;
}

type ApprovalHierarchyNodes = ApprovalsInfo_approvals_nodes & {
  order: number;
  priority: number;
};

const getItemStyle = (
  isDragging: boolean,
  draggableStyle: any,
  index: number,
  theme: Theme
) => ({
  ...draggableStyle,
  border: `1px solid ${theme.palette.neutralLight}`,
  borderRadius: 3,
  background: !isDragging ? theme.palette.white : theme.palette.neutralLighter,
});

// a little function to help us with reordering the result
const reorder = (list: any, startIndex: number, endIndex: number) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
};

export const ReorderHierarchy: React.FC<ReorderHierarchyProps> = (props) => {
  const styles = useStyles();
  const theme = useTheme();

  const [resequenceApprovals] = useMutation<
    ApprovalResequencing,
    ApprovalResequencingVariables
  >(APPROVAL_RESEQUENCING, {
    errorPolicy: 'all',
  });

  const [priorityList, setPriorityList] = useState<ApprovalHierarchyNodes[]>(
    []
  );

  const [hasManuallyReordered, setHasManuallyReordered] = useState(false);

  const onDragEnd = async (result: DropResult, provided: ResponderProvided) => {
    const { destination, source } = result;

    if (!destination) {
      // dropped outside the list
      return;
    }

    const chartNodesNew: ApprovalHierarchyNodes[] = reorder(
      priorityList,
      source.index,
      destination.index
    ) as ApprovalHierarchyNodes[];

    const sourceItem = chartNodesNew[source.index];
    if (
      sourceItem.badgeColor === 'RED' &&
      Math.abs(source.index - destination.index) === 1
    )
      return;

    const movedItem = chartNodesNew[destination.index];
    const priorityNumbersList = priorityList
      .filter(
        (approvalFlow) =>
          movedItem.badgeColor === 'RED' || approvalFlow.badgeColor !== 'RED'
      )
      .map((approvalFlow) => approvalFlow.priorityNumber);

    // Save previous state for rollback
    const previousList = [...priorityList];

    const updatedList = chartNodesNew.map((item, index) =>
      item === movedItem
        ? { ...item, order: index + 1 }
        : { ...item, order: index + 1 }
    );

    // Optimistically update UI
    setPriorityList(updatedList);
    setHasManuallyReordered(true);
    const filteredApprovals =
      movedItem.badgeColor !== 'RED'
        ? updatedList.filter((approval) => approval.badgeColor !== 'RED')
        : updatedList;

    const resequence: ResequenceTypeInput[] = filteredApprovals.map(
      (approval, index) => ({
        id: approval.id!,
        rowTimestamp: approval._rowTimestamp!,
        sequenceNumber: priorityNumbersList[index],
      })
    );

    // Try mutation
    const { errors } = await resequenceApprovals({
      variables: {
        input: {
          resequence,
        },
      },
    });

    if (errors?.length) {
      // Revert state on failure
      setPriorityList(previousList);
      setHasManuallyReordered(false);

      showCustomToast({
        content: errors[0].message,
        appearance: 'error',
      });
    } else {
      showCustomToast({
        content: 'Hierarchy updated',
        appearance: 'success',
      });
      props.refetch();
    }
  };

  useEffect(() => {
    if (hasManuallyReordered) {
      setHasManuallyReordered(false);
      return; // prevent override after reorder
    }

    const data: ApprovalHierarchyNodes[] =
      props.approvalsInfo?.approvals?.nodes.map((item, index) => {
        return {
          ...item,
          order: index + 1,
          priority: Number(item._infoLines?.infoLine3),
        } as ApprovalHierarchyNodes;
      }) || [];
    setPriorityList(data);
  }, [props.approvalsInfo]);

  const commonStyles = useCommonStyles();
  const PASSED_TEST = 'Passed All Tests';

  return (
    <Stack tokens={{ padding: '20px 0px' }}>
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="droppable">
          {(provided) => (
            <div {...provided.droppableProps} ref={provided.innerRef}>
              <>
                {priorityList?.map((item, index) => {
                  return (
                    <>
                      <Draggable
                        key={index.toString()}
                        draggableId={index.toString()}
                        index={index}
                      >
                        {(provided, snapshot) => {
                          const { isDragging } = { ...snapshot };
                          return (
                            <div
                              className={isDragging ? styles.root : ''}
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              style={getItemStyle(
                                isDragging,
                                provided.draggableProps.style,
                                index,
                                theme
                              )}
                            >
                              <Stack
                                style={{
                                  borderLeft: `15px solid ${
                                    ColorHighLights.find(
                                      (it) => it.id === item.badgeColor
                                    )?.color
                                  }`,
                                }}
                                key={index.toFixed()}
                              >
                                <Stack
                                  horizontal
                                  horizontalAlign="space-between"
                                  verticalAlign="center"
                                  tokens={{ padding: '10px 20px' }}
                                >
                                  <Stack
                                    horizontal
                                    verticalAlign="center"
                                    tokens={{ childrenGap: 20 }}
                                  >
                                    <FontIcon iconName="GripperDotsVertical" />
                                    <Stack>
                                      <Link
                                        onClick={() => {
                                          props.onRowOpen(item.id);
                                        }}
                                        className={commonStyles.noSelect}
                                      >
                                        <Text
                                          className={
                                            commonStyles.colorThemePrimary
                                          }
                                          variant="xLarge"
                                        >
                                          {item._fullApprovalDescription}
                                        </Text>
                                      </Link>
                                    </Stack>
                                  </Stack>

                                  <Stack
                                    style={{
                                      marginLeft: 'auto',
                                      width: 450,
                                      marginRight: 200,
                                    }}
                                  >
                                    {(() => {
                                      const infoLine1 =
                                        item._infoLines?.infoLine1;
                                      if (!infoLine1) return null;

                                      const displayText =
                                        infoLine1.length > 70
                                          ? `${infoLine1.slice(0, 67)}...`
                                          : infoLine1;
                                      return (
                                        <TooltipHost
                                          content={infoLine1}
                                          directionalHint={
                                            DirectionalHint.topLeftEdge
                                          }
                                        >
                                          <Text>{displayText}</Text>
                                        </TooltipHost>
                                      );
                                    })()}
                                  </Stack>

                                  <Stack horizontal verticalAlign="center">
                                    <Stack
                                      horizontal
                                      horizontalAlign="center"
                                      verticalAlign="center"
                                      style={{
                                        width: 80,
                                      }}
                                    >
                                      {item._infoLines?.isTested && (
                                        <TooltipHost
                                          content={PASSED_TEST}
                                          id="tooltipId"
                                        >
                                          <IconButton
                                            iconProps={{
                                              iconName: 'CompletedSolid',
                                            }}
                                          />
                                        </TooltipHost>
                                      )}
                                      {!!item._infoLines?.activeTestCount && (
                                        <Text>
                                          {item._infoLines?.activeTestCount}
                                        </Text>
                                      )}
                                    </Stack>
                                    <Stack
                                      horizontalAlign="end"
                                      style={{ width: 200 }}
                                    >
                                      <Stack
                                        verticalAlign="center"
                                        horizontalAlign="center"
                                        horizontal
                                      >
                                        <Stack
                                          style={{ transform: 'scale(0.8)' }}
                                        >
                                          <StatusIcon
                                            approvalData={item}
                                            approval={false}
                                            iconType={item?.statusType!}
                                          />
                                        </Stack>
                                        <Stack>
                                          <Text>
                                            {item?.statusType?.statusType!}
                                          </Text>
                                        </Stack>
                                      </Stack>
                                    </Stack>
                                  </Stack>
                                </Stack>
                              </Stack>
                            </div>
                          );
                        }}
                      </Draggable>
                    </>
                  );
                })}
              </>

              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </Stack>
  );
};
